import React, { useCallback, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import illustration
  from '../../../../assets/changeCoverage/dog-cat-woman-sitting-playing.svg';

import {
  CUSTOMER_EXPERIENCE_PHONE_NUMBER,
} from '../../../claims/claimbot/claimbot.constants';
import { POST_MESSAGE_TYPES } from '../../../../constants';
import { useContactUsToggler, useMixpanel, usePostMessage } from '../../../../hooks';
import { EV1 } from '../../../../services/mixpanel';
import { getBrandNameByChannelId } from '../../../../services/utils';
import {
  getPMSByMarketChannelId,
} from '../../../../services/multibrand.utils';
import { INSURANCE } from '../../../navigation/routes.constants';
import { formatDate } from '../../coverageOverview/changeCoverage.utils';
import { useChangeCoveragePrice } from '../hooks';

import styles from './ChangeCoverageSubmitted.module.css';

const ChangeCoverageSubmitted = ({ isWebView = false }) => {
  const { t } = useTranslation('changeCoverage');
  const history = useHistory();

  const { policyNumber } = useParams();
  const { sendEvent } = useMixpanel();
  const postMessage = usePostMessage();
  const { price: monthlyTotal } = useChangeCoveragePrice();
  const { onContactUsToggle } = useContactUsToggler();
  const store = useSelector(({ policies }) => ({
    allPolicies: policies.allPolicies,
    changeCoverage: policies.changeCoverage,
  }), shallowEqual);
  const { allPolicies, changeCoverage } = store;

  const { pms, policyBrandName } = useMemo(() => {
    const foundPolicy = allPolicies
      ?.find((policyItem) => policyItem.Policy.Number === policyNumber);
    const marketingChannelId = foundPolicy?.Policy.ProductFamilyId ?? '';

    return {
      pms: getPMSByMarketChannelId(marketingChannelId),
      policyBrandName: getBrandNameByChannelId(marketingChannelId),
    };
  }, [allPolicies, policyNumber]);

  const onBackToInsurance = useCallback(() => {
    if (isWebView) {
      postMessage(POST_MESSAGE_TYPES.CLOSE_PAGE);
      return;
    }

    const source = window.location.pathname;
    sendEvent(EV1({ pms, policyBrand: policyBrandName, source }));
    history.push(INSURANCE, { changeCoverageId: policyNumber });
  }, [isWebView, pms, policyBrandName, policyNumber, postMessage, sendEvent]);

  return (
    <div className={styles.container}>
      <img alt="" className={styles.illustration} src={illustration} />

      <h3 className={styles.title}>{t('submitted.title')}</h3>

      <div className={styles.summary}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>{t('submitted.plan')}</span>

          <span className={styles.summaryValue}>
            {changeCoverage.quoteSelectedValues?.coverageName}
          </span>
        </div>

        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>
            {t('submitted.effectiveDate')}
          </span>

          <span className={styles.summaryValue}>
            {formatDate(changeCoverage.rate?.effectiveDate)}
          </span>
        </div>

        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>
            {t('submitted.newMonthlyTotal')}
          </span>

          <span className={styles.summaryValue}>{monthlyTotal}</span>
        </div>
      </div>

      <p className={styles.contactMessage}>
        <Trans
          components={{ bold: <strong /> }}
          i18nKey="submitted.contactMessage"
          ns="changeCoverage"
          values={{ phone: CUSTOMER_EXPERIENCE_PHONE_NUMBER }}
        />
      </p>

      <div className={styles.buttonsContainer}>
        <button
          className={`${styles.baseButton } ${ styles.backButton}`}
          onClick={onBackToInsurance}
          type="button"
        >
          {t('submitted.backToInsurance')}
        </button>

        <button
          className={`${styles.baseButton } ${ styles.contactButton}`}
          onClick={onContactUsToggle}
          type="button"
        >
          {t('submitted.contactUs')}
        </button>
      </div>
    </div>
  );
};

export { ChangeCoverageSubmitted };
